import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject,OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';

import { AnnouncementModalActionsEnum } from '../../definitions/announcement-modal.enum';
import { Announcement } from '../../models/announcement';
import { AnnouncementService } from '../../services/announcement.service';
import { CustomSnackbarService } from '../../services/custom-snackbar.service';
import { defaultLang,SUPPORTED_LANGS  } from '../../utils/translate';
import { AnnouncementDisableModalComponent } from './announcement-disable-modal/announcement-disable-modal.component';
// eslint-disable-next-line max-len
import { CreateModifyAnnouncementModalComponent } from './create-modify-announcement-modal/create-modify-announcement-modal.component';

@Component({
  selector: 'app-announcement-management',
  templateUrl: './announcement-management.component.html',
  styleUrls: ['./announcement-management.component.scss']
})
export class AnnouncementManagementComponent implements OnInit {
  private datePipe = inject(DatePipe);
  private dialogService = inject(MatDialog);
  private translate = inject(TranslateService);
  private snackbarService = inject(CustomSnackbarService);
  private announcementService = inject(AnnouncementService);

  protected readonly announcementModalActionsEnum = AnnouncementModalActionsEnum;

  /**
   * Current active announcement
   */
  currentAnnouncement?: Announcement;

  async ngOnInit(): Promise<void> {
    this.getCurrentAnnouncement();
  }

  /**
   * Get current active announcement
   */
  async getCurrentAnnouncement(): Promise<void> {
    try {
      this.currentAnnouncement = await this.announcementService.getCurrentActiveAnnouncement().toPromise();
    } catch (err: any) {
      if (err?.error?.errors === 'API.Announcement.No-active-announcement') {
        this.currentAnnouncement = undefined;
      } else if (err instanceof HttpErrorResponse) {
        this.snackbarService.showTranslatedError(err);
      }
    }
  }

  /**
   * Method to open the dialog to create/modify a announcement
   */
  openCreateModifyAnnouncementModal(action: AnnouncementModalActionsEnum): void {
    const dialogRef = this.dialogService.open(CreateModifyAnnouncementModalComponent, {
      width: '55.1rem',
      height: 'fit-content',
      data: {
        action: action
      }
    });

    dialogRef.afterClosed().subscribe((success: boolean) => {
      if (success) {
        this.getCurrentAnnouncement();
        this.snackbarService.present(
          this.translate.instant(
            action === AnnouncementModalActionsEnum.CREATE
              ? 'announcements.createModal.successMessage'
              : 'announcements.modifyModal.successMessage'
          )
        );
      }
    });
  }

  /**
   * Method to open the dialog to disable the current active announcement
   */
  openDisableAnnouncementModal(): void {
    const dialogRef = this.dialogService.open(AnnouncementDisableModalComponent, {
      width: '33.6rem',
      height: 'fit-content'
    });

    dialogRef.afterClosed().subscribe((success: boolean) => {
      if (success) {
        this.currentAnnouncement = undefined;
        this.snackbarService.present(this.translate.instant('announcements.disableModal.successMessage'));
      }
    });
  }

  /**
   * Method to extract the day, month and year from a date
   * @param date
   */
  getDayMonthYear(date: Date): string {
    return this.datePipe.transform(date, 'dd/MM/yyyy') as string;
  }

  /**
   * Method to extract the hour and minute from a date
   * @param date
   */
  getHourMinute(date: Date): string {
    return this.datePipe.transform(date, 'HH:mm') as string;
  }

  /**
   * Method to extract the announcement body based on the user's language.
   */
  getAnnouncementBodyByLanguage(): string {
    if (!this.currentAnnouncement) {
      return '';
    }

    const lang = this.translate.getDefaultLang() ?? defaultLang;
    const bodyMap: { [key: string]: string } = {
      [SUPPORTED_LANGS.es]: this.currentAnnouncement.body.es,
      [SUPPORTED_LANGS.en]: this.currentAnnouncement.body.en,
    };

    return bodyMap[lang] ?? bodyMap[SUPPORTED_LANGS.en];

  }

}
